/**
 * Settings → Broker: pair a MetaTrader terminal with the mt-ea bridge.
 * Beta-only (beta-widgets role); the tab link is also hidden in Settings.tsx.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import clsx from "clsx";
import {
  CheckIcon,
  ClipboardDocumentIcon,
  NoSymbolIcon,
  SignalIcon,
  SignalSlashIcon,
} from "@heroicons/react/24/outline";
import { useHasBetaWidgetsRole } from "@/hooks/useHasBetaWidgetsRole";
import {
  baseURL,
  createPairing,
  getAccount,
  revokePairing,
  setAllowLive,
} from "@/shared/api/mt-ea";
import { safeClipboardWrite } from "@/utils/clipboard";
import { useBrokerStore } from "@/stores/BrokerStore";

const EA_DOWNLOADS: Record<4 | 5, { label: string; href: string }> = {
  4: { label: "NewsquawkBridge.ex4 (MetaTrader 4)", href: `${baseURL}/downloads/NewsquawkBridge.ex4` },
  5: { label: "NewsquawkBridge.ex5 (MetaTrader 5)", href: `${baseURL}/downloads/NewsquawkBridge.ex5` },
};

export default function Broker() {
  const hasBetaRole = useHasBetaWidgetsRole();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("My broker");
  const [mtVersion, setMtVersion] = useState<4 | 5>(4);
  // Shown exactly once after pairing — never retrievable again.
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const quickTrade = useBrokerStore((s) => s.quickTrade);
  const setQuickTrade = useBrokerStore((s) => s.setQuickTrade);
  const [guideMode, setGuideMode] = useState<"demo" | "live">("demo");

  const accountQuery = useQuery({
    queryKey: ["mt-ea", "account"],
    queryFn: getAccount,
    refetchInterval: 5000,
    enabled: hasBetaRole,
  });

  const pairMutation = useMutation({
    mutationFn: () => createPairing(label, mtVersion),
    onSuccess: (data) => {
      setFreshToken(data.pairing_token);
      queryClient.invalidateQueries({ queryKey: ["mt-ea", "account"] });
    },
    onError: () => toast.error("Could not create the broker connection."),
  });

  const allowLiveMutation = useMutation({
    mutationFn: setAllowLive,
    onSuccess: (_, enabled) => {
      queryClient.invalidateQueries({ queryKey: ["mt-ea", "account"] });
      toast.success(enabled ? "Live trading enabled." : "Live trading disabled.");
    },
    onError: () => toast.error("Could not update live-trading setting."),
  });

  const revokeMutation = useMutation({
    mutationFn: revokePairing,
    onSuccess: () => {
      setFreshToken(null);
      queryClient.invalidateQueries({ queryKey: ["mt-ea", "account"] });
      toast.success("Broker disconnected.");
    },
    onError: () => toast.error("Could not disconnect."),
  });

  if (!hasBetaRole) {
    return (
      <div className="px-4 py-16 sm:px-6 lg:px-8 text-sm text-gray-500 dark:text-gray-400">
        Broker connectivity is currently in closed beta.
      </div>
    );
  }

  const account = accountQuery.data;

  const copyToken = async () => {
    if (!freshToken) return;
    await safeClipboardWrite(freshToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="divide-y divide-gray-200 dark:divide-white/5">
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Broker connection
            <span className="ml-2 rounded-full bg-squawk-blue/10 px-2 py-0.5 text-xs font-medium text-squawk-blue">
              Beta
            </span>
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Trade from Newsquawk widgets through your own MetaTrader terminal.
            Your broker credentials never leave your machine — the widgets talk
            to a Newsquawk EA running in your terminal via a pairing token.
          </p>
        </div>

        <div className="md:col-span-2">
          {/* ---- loading / error ---- */}
          {accountQuery.isPending && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Checking your broker connection…
            </p>
          )}
          {accountQuery.isError && (
            <div className="max-w-xl rounded-lg border border-red-400/40 bg-red-50 dark:bg-red-400/5 p-4">
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                Couldn't reach the broker service
              </p>
              <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                {(accountQuery.error as any)?.response?.data?.detail ||
                  (accountQuery.error as Error)?.message ||
                  "Unknown error"}
              </p>
              <button
                type="button"
                onClick={() => accountQuery.refetch()}
                className="mt-3 rounded-md bg-white dark:bg-white/10 px-3 py-1.5 text-xs font-semibold text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-white/10 hover:bg-gray-50 dark:hover:bg-white/20"
              >
                Retry
              </button>
            </div>
          )}

          {/* ---- no account: pairing form ---- */}
          {accountQuery.isSuccess && !account && !freshToken && (
            <div className="max-w-xl space-y-6">
              <div>
                <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">
                  Connection name
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  className="mt-2 block w-full rounded-md border-0 bg-white dark:bg-white/5 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-white/10 focus:ring-2 focus:ring-inset focus:ring-squawk-blue sm:text-sm/6"
                />
              </div>
              <div>
                <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">
                  MetaTrader version
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  IG and Capital.com accounts use MetaTrader 4.
                </p>
                <div className="mt-2 flex gap-3">
                  {([4, 5] as const).map((version) => (
                    <button
                      key={version}
                      type="button"
                      onClick={() => setMtVersion(version)}
                      className={clsx(
                        "rounded-md px-4 py-2 text-sm font-semibold ring-1 ring-inset transition",
                        mtVersion === version
                          ? "bg-squawk-blue text-white ring-squawk-blue"
                          : "bg-white dark:bg-white/5 text-gray-900 dark:text-white ring-gray-300 dark:ring-white/10 hover:ring-squawk-blue"
                      )}
                    >
                      MetaTrader {version}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                disabled={pairMutation.isPending}
                onClick={() => pairMutation.mutate()}
                className="rounded-md bg-squawk-blue px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-squawk-blue-light disabled:opacity-50 transition"
              >
                {pairMutation.isPending ? "Creating…" : "Connect a broker"}
              </button>
            </div>
          )}

          {/* ---- fresh token: one-time reveal + setup steps ---- */}
          {freshToken && (
            <div className="max-w-2xl space-y-6">
              <div className="rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-400/5 p-4">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  Copy your pairing token now — it is shown only once.
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-white dark:bg-black/30 px-3 py-2 font-mono text-xs text-gray-900 dark:text-gray-100">
                    {freshToken}
                  </code>
                  <button
                    type="button"
                    onClick={copyToken}
                    className="inline-flex items-center gap-1 rounded-md bg-white dark:bg-white/10 px-3 py-2 text-xs font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-white/10 hover:bg-gray-50 dark:hover:bg-white/20"
                  >
                    {copied ? (
                      <CheckIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              <ol className="list-decimal space-y-3 pl-5 text-sm/6 text-gray-700 dark:text-gray-300">
                <li>
                  Download the Newsquawk EA:{" "}
                  <a
                    href={EA_DOWNLOADS[mtVersion].href}
                    className="font-medium text-squawk-blue hover:text-squawk-blue-light"
                  >
                    {EA_DOWNLOADS[mtVersion].label}
                  </a>
                </li>
                <li>
                  In MetaTrader, open{" "}
                  <span className="font-medium">File → Open Data Folder</span> and copy the file
                  into <code className="text-xs">MQL{mtVersion}/Experts</code>, then restart the terminal.
                </li>
                <li>
                  Open <span className="font-medium">Tools → Options → Expert Advisors</span>, tick{" "}
                  <span className="font-medium">Allow WebRequest for listed URL</span> and add:{" "}
                  <code className="rounded bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 text-xs">{baseURL}</code>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Without this step the EA cannot reach Newsquawk — it will show
                    “BLOCKED” on the chart.
                  </span>
                </li>
                <li>
                  Drag <span className="font-medium">NewsquawkBridge</span> onto any chart, paste the
                  pairing token into the <code className="text-xs">PairingToken</code> input, and make
                  sure <span className="font-medium">AutoTrading</span> is enabled.
                </li>
                <li>
                  The chart shows <span className="font-medium">“CONNECTED”</span> and this page
                  updates within a few seconds.
                </li>
              </ol>

              <button
                type="button"
                onClick={() => setFreshToken(null)}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Done — I've stored the token
              </button>
            </div>
          )}

          {/* ---- paired: status card ---- */}
          {account && (
            <div className="max-w-xl space-y-6">
              <div className="rounded-lg border border-gray-200 dark:border-white/10 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {account.connected ? (
                      <SignalIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <SignalSlashIcon className="h-5 w-5 text-gray-400" />
                    )}
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {account.label}
                    </span>
                  </div>
                  <span
                    className={clsx(
                      "rounded-full px-2 py-0.5 text-xs font-medium",
                      account.connected
                        ? "bg-green-500/10 text-green-600 dark:text-green-400"
                        : "bg-gray-500/10 text-gray-500 dark:text-gray-400"
                    )}
                  >
                    {account.connected
                      ? "Connected"
                      : account.status === "pending"
                        ? "Waiting for terminal"
                        : "Terminal offline"}
                  </span>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-gray-500 dark:text-gray-400">Broker server</dt>
                  <dd className="text-gray-900 dark:text-white">{account.broker_server || "—"}</dd>
                  <dt className="text-gray-500 dark:text-gray-400">Account</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {account.mt_account_number || "—"} (MT{account.mt_version})
                  </dd>
                  <dt className="text-gray-500 dark:text-gray-400">Balance</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {account.state
                      ? `${account.state.balance.toLocaleString()} ${account.state.currency}`
                      : "—"}
                  </dd>
                  <dt className="text-gray-500 dark:text-gray-400">Max order size</dt>
                  <dd className="text-gray-900 dark:text-white">{account.max_lots} lots</dd>
                </dl>
                {!account.connected && account.status === "connected" && (
                  <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
                    Open MetaTrader with the NewsquawkBridge EA attached to resume trading.
                  </p>
                )}
                {account.kill_switch && (
                  <p className="mt-3 text-xs text-red-500">
                    Trading is paused on this connection by Newsquawk support.
                  </p>
                )}
                {account.live_blocked && (
                  <div className="mt-3 rounded-md border border-red-400/40 bg-red-50 dark:bg-red-400/5 p-3">
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Your terminal is logged into a{" "}
                      <span className="font-semibold">live</span> account (
                      {account.broker_server}) — trading real money. Orders are blocked
                      until you explicitly enable live trading, or switch MetaTrader to a
                      demo login (File → Login to Trade Account).
                    </p>
                    <button
                      type="button"
                      disabled={allowLiveMutation.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Enable LIVE trading on ${account.broker_server} (account ${account.mt_account_number})?\n\nOrders placed from Newsquawk widgets will use REAL MONEY on this account.`
                          )
                        ) {
                          allowLiveMutation.mutate(true);
                        }
                      }}
                      className="mt-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-50"
                    >
                      I understand — enable live trading
                    </button>
                  </div>
                )}
                {account.live_server && account.allow_live && (
                  <div className="mt-3 flex items-center justify-between rounded-md bg-amber-500/10 px-3 py-2">
                    <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                      LIVE trading enabled — widget orders use real money
                    </span>
                    <button
                      type="button"
                      disabled={allowLiveMutation.isPending}
                      onClick={() => allowLiveMutation.mutate(false)}
                      className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Disable
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                disabled={revokeMutation.isPending}
                onClick={() => {
                  if (window.confirm("Disconnect this broker? The EA will stop working until re-paired.")) {
                    revokeMutation.mutate();
                  }
                }}
                className="inline-flex items-center gap-2 rounded-md bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition"
              >
                <NoSymbolIcon className="h-4 w-4" />
                Disconnect broker
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ---- headline quick trade ---- */}
      {account && (
        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
              Headline quick trade
            </h2>
            <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
              Show one-click Buy/Sell buttons directly on headlines whose tags map
              to a tradable instrument. Orders fire immediately at market with your
              default size — no confirmation step.
            </p>
          </div>
          <div className="md:col-span-2 max-w-xl space-y-4">
            <label className="flex items-center gap-3 text-sm text-gray-900 dark:text-white">
              <input
                type="checkbox"
                checked={quickTrade.enabled}
                onChange={(e) => setQuickTrade({ enabled: e.target.checked })}
                className="form-checkbox rounded text-squawk-blue"
              />
              Enable Buy/Sell buttons on headlines
            </label>
            <div>
              <label className="block text-sm/6 font-medium text-gray-900 dark:text-white">
                Default order size (lots)
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={quickTrade.lots}
                onChange={(e) =>
                  setQuickTrade({ lots: Math.max(0.01, parseFloat(e.target.value) || 0.01) })
                }
                className="mt-2 block w-32 rounded-md border-0 bg-white dark:bg-white/5 py-1.5 px-2 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-white/10 focus:ring-2 focus:ring-inset focus:ring-squawk-blue sm:text-sm/6 tabular-nums"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Buttons appear once a Broker widget is on your dashboard (that's what
              holds the live broker connection) and only on headlines tagged with an
              instrument your broker trades. They respect the same guards as the
              ticket: offline, live-account, and kill-switch states disable them.
            </p>
          </div>
        </div>
      )}

      {/* ---- full setup guide, always visible ---- */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Setup guide
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            {guideMode === "demo"
              ? "Never used MetaTrader? Start here — about 15 minutes on a free demo account, no real money involved."
              : "Connect the MetaTrader login your broker gave you for your funded account. Orders placed from Newsquawk widgets use real money."}
          </p>
          <div className="mt-4 inline-flex rounded-lg bg-gray-100 dark:bg-white/5 p-0.5">
            {(["demo", "live"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setGuideMode(mode)}
                className={clsx(
                  "rounded-md px-4 py-1.5 text-xs font-semibold transition",
                  guideMode === mode
                    ? mode === "live"
                      ? "bg-amber-500 text-black"
                      : "bg-squawk-blue text-white"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                )}
              >
                {mode === "demo" ? "Demo account" : "Live account"}
              </button>
            ))}
          </div>
          {guideMode === "live" && (
            <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
              Live connections are blocked until you explicitly enable live trading
              on this page — an extra deliberate step so nothing trades real money
              by accident.
            </p>
          )}
        </div>
        <div className="md:col-span-2 max-w-2xl space-y-6 text-sm/6 text-gray-700 dark:text-gray-300">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">How it works</h3>
            <p className="mt-1">
              MetaTrader is a desktop trading terminal your broker gives you a login
              for. Our <span className="font-medium">NewsquawkBridge</span> EA
              (Expert Advisor) is a small plugin that runs inside it: the widgets
              send orders to Newsquawk, the EA executes them on your account and
              streams prices back. If MetaTrader isn't running, nothing executes —
              queued orders expire after 15 seconds rather than firing late.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {guideMode === "demo"
                ? "1 · Get a demo account with an MT4 login"
                : "1 · Get your broker's MetaTrader login for your funded account"}
            </h3>
            {guideMode === "demo" ? (
              <p className="mt-1">
                You need a <span className="font-medium">server name, login number and
                password</span> for MetaTrader — a normal web-platform demo login is not
                the same thing. With IG: in My IG use{" "}
                <span className="font-medium">Add an account → MetaTrader 4</span> (if
                you don't see MT4 offered, finish activating your account first —
                IG hides it until then). Fastest alternative for a pure test:
                a Pepperstone or IC Markets MT4 demo — instant credentials by email.
              </p>
            ) : (
              <p className="mt-1">
                You need the <span className="font-medium">server name, login number and
                password</span> your broker issued for MetaTrader on your{" "}
                <span className="font-medium">funded</span> account — your normal
                website login is not the same thing. With IG:{" "}
                <span className="font-medium">My IG → Add an account → MetaTrader 4</span>{" "}
                creates the MT4 account and emails the credentials (server{" "}
                <code className="text-xs">IG-LIVE</code>). Funds must be transferred to
                the MT4 account within your broker's dashboard before it can trade. We
                recommend completing the full demo run-through first.
              </p>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              2 · Log the terminal in
            </h3>
            <p className="mt-1">
              In MetaTrader: <span className="font-medium">File → Login to Trade
              Account</span> — enter the login, password, and exact server name from
              your broker's email. Prices ticking in the Market Watch panel means
              you're connected.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              3 · Install the EA
            </h3>
            <p className="mt-1">
              Connect above to get your pairing token and the EA download. Then in
              MetaTrader: <span className="font-medium">File → Open Data Folder</span>,
              copy the file into <code className="text-xs">MQL4/Experts</code> (or{" "}
              <code className="text-xs">MQL5/Experts</code>), and restart the
              terminal. It appears in the Navigator panel under{" "}
              <span className="font-medium">Expert Advisors → NewsquawkBridge</span> —
              it's ours, so it's never in MetaTrader's built-in Market store.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              4 · The two settings everyone misses
            </h3>
            <p className="mt-1">
              <span className="font-medium">Tools → Options → Expert Advisors</span>:
              tick <span className="font-medium">Allow automated trading</span> and{" "}
              <span className="font-medium">Allow WebRequest for listed URL</span>,
              adding{" "}
              <code className="rounded bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 text-xs">
                {baseURL}
              </code>
              . Also make sure the <span className="font-medium">AutoTrading</span>{" "}
              toolbar button is green. Miss either and the chart will tell you —
              "BLOCKED" for the URL, a frowning face for AutoTrading.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              5 · Attach to any chart & trade
            </h3>
            <p className="mt-1">
              Open any chart and drag NewsquawkBridge onto it from the Navigator,
              then paste your pairing token into the{" "}
              <code className="text-xs">PairingToken</code> input. Which chart is
              irrelevant — the EA trades whatever the widget asks, on any symbol,
              regardless of the chart it lives on. When the chart's top-left corner
              says <span className="font-medium">CONNECTED</span>, add the{" "}
              <span className="font-medium">Broker Ticket</span> and{" "}
              <span className="font-medium">Broker Positions</span> widgets to your
              dashboard and place a trade — the fill lands in under a second, and
              the auto-switch flag lets headline tags drive which asset is loaded.
            </p>
          </div>
          {guideMode === "live" && (
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">
                6 · Explicitly enable live trading
              </h3>
              <p className="mt-1">
                Because the terminal is on a live server, this page will show the
                connection as <span className="font-medium">blocked</span> with an{" "}
                <span className="font-medium">"I understand — enable live trading"</span>{" "}
                button. Orders stay refused until you press it. Once enabled, every
                broker widget carries a permanent amber{" "}
                <span className="font-medium">LIVE ACCOUNT</span> banner, and you can
                disable again here at any time. Your per-order size cap ({account
                  ? `${account.max_lots} lots`
                  : "set by Newsquawk"}) applies to every order.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ---- troubleshooting ---- */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Troubleshooting
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            The chart's top-left status line and the widget's order toasts tell you
            what's wrong — here's what each one means.
          </p>
        </div>
        <div className="md:col-span-2 max-w-2xl">
          <dl className="divide-y divide-gray-100 dark:divide-white/5 text-sm/6">
            {[
              {
                symptom: "Order rejected: “automated trading disabled”",
                fix: "MetaTrader refused the order locally — the broker never saw it. Two switches must both be on: the AutoTrading toolbar button (green ▶), and right-click the chart → Expert Advisors → Properties → Common tab → “Allow live trading”. Fix, then simply retry.",
              },
              {
                symptom: "Chart says BLOCKED",
                fix: `The EA can't reach Newsquawk. Tools → Options → Expert Advisors → tick “Allow WebRequest for listed URL” and add ${baseURL} exactly (https, no trailing slash).`,
              },
              {
                symptom: "Chart says INVALID TOKEN",
                fix: "Disconnect and reconnect above to get a fresh pairing token, then re-enter it in the EA inputs (right-click chart → Expert Advisors → Properties → Inputs).",
              },
              {
                symptom: "Widget says “terminal offline”",
                fix: "MetaTrader isn't running, the EA isn't attached to a chart, or the computer is asleep. Orders queued while offline expire after 15 seconds — they never fire late.",
              },
              {
                symptom: "Order rejected: “symbol not available”",
                fix: "Your broker doesn't offer that instrument, or names it unusually. Contact us — we can map it to your broker's naming on our side.",
              },
              {
                symptom: "Order rejected: “market closed” / “off quotes”",
                fix: "The broker's market for that instrument isn't open (weekends, session breaks) — nothing to fix on your side.",
              },
              {
                symptom: "Buy/Sell buttons greyed out",
                fix: "Terminal offline, an order is in flight, live trading not yet enabled on a live account, or trading is paused by Newsquawk support (shown as a banner).",
              },
            ].map((row) => (
              <div key={row.symptom} className="py-3">
                <dt className="font-medium text-gray-900 dark:text-white">{row.symptom}</dt>
                <dd className="mt-1 text-gray-600 dark:text-gray-400">{row.fix}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
